import { useState } from "react";
import { api, API, getToken, formatApiErrorDetail } from "@/lib/api";
import { HeartHandshake, Upload, CheckCircle2 } from "lucide-react";
import { t } from "@/lib/i18n";

const AssistanceApplication = ({ onSubmitted }) => {
  const [form, setForm] = useState({
    contact_name: "",
    phone: "",
    address: "",
    best_time: "anytime",
    notes: "",
  });
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (file) fd.append("proof", file);
      const res = await fetch(`${API}/assistance/apply`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw { response: { data } };
      setDone(data);
      onSubmitted?.(data);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || "Submission failed.");
    } finally {
      setSaving(false);
    }
  };

  if (done) {
    return (
      <div className="card-flat" data-testid="assistance-app-done">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={18} className="text-[#2F524A]" />
          <h3 className="font-serif text-xl">{t("Application submitted")}</h3>
        </div>
        <p className="text-sm text-[#5C6661] mt-2">
          {t("Our team will reach out to")}{" "}<b>{done.contact_name}</b>{" "}{t("at")}{" "}<b>{done.phone}</b>. {t("Best time to call")}: {done.best_time || t("anytime")}.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-flat space-y-4" data-testid="assistance-app-form">
      <div className="flex items-center gap-2">
        <HeartHandshake size={16} className="text-[#2F524A]" />
        <h3 className="font-serif text-xl">{t("Apply for mitigation assistance")}</h3>
      </div>
      <p className="text-sm text-[#5C6661]">
        {t("Fill in your contact details and (optionally) upload proof of income. Your screening answers are already attached.")}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="eyebrow block mb-2">{t("Contact name *")}</label>
          <input required className="input-field" value={form.contact_name}
            onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
            data-testid="app-contact-name" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Phone *")}</label>
          <input required className="input-field" value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="412-555-0100" data-testid="app-phone" />
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow block mb-2">{t("Mailing address *")}</label>
          <input required className="input-field" value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
            data-testid="app-address" />
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Best time to call")}</label>
          <select className="input-field" value={form.best_time}
            onChange={(e) => setForm((f) => ({ ...f, best_time: e.target.value }))}
            data-testid="app-best-time">
            <option value="anytime">{t("Anytime")}</option>
            <option value="morning">{t("Morning")}</option>
            <option value="afternoon">{t("Afternoon")}</option>
            <option value="evening">{t("Evening")}</option>
          </select>
        </div>
        <div>
          <label className="eyebrow block mb-2">{t("Proof of income (optional)")}</label>
          <label className="inline-flex items-center gap-2 cursor-pointer input-field !py-2 text-sm text-[#5C6661]" data-testid="app-proof-wrapper">
            <Upload size={14} />
            {file ? file.name: t("Choose a file (PDF or image, ≤ 5 MB)")}
            <input type="file" className="hidden" accept=".pdf,image/*"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="app-proof-input" />
          </label>
        </div>
        <div className="sm:col-span-2">
          <label className="eyebrow block mb-2">{t("Notes (optional)")}</label>
          <textarea className="input-field" rows={3} value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            maxLength={500} data-testid="app-notes" />
        </div>
      </div>
      {error && <div className="text-sm text-[#9B2C2C] bg-[#FCEDED] border border-[#F5C2C2] rounded-lg px-3 py-2">{error}</div>}
      <div className="flex justify-end">
        <button type="submit" disabled={saving} className="btn-primary !py-2 !px-5 text-sm"
          data-testid="app-submit">
          {saving ? t("Submitting…") : t("Submit application")}
        </button>
      </div>
    </form>
  );
};

export default AssistanceApplication;
